<template>
  <teleport to="body">
    <div v-if="show" class="epm-overlay" @click.self="close">
      <div class="epm-modal" role="dialog" aria-modal="true" aria-labelledby="epm-title">
        <!-- Header -->
        <header class="epm-header">
          <h3 id="epm-title" class="epm-title">Добавить элемент</h3>
          <button class="epm-close" @click="close" aria-label="close">×</button>
        </header>

        <!-- Body -->
        <section class="epm-body">
          <!-- Categories -->
          <div class="epm-categories">
            <ul>
              <li v-for="(cat, idx) in categories" :key="cat.label" :class="{active: idx === activeCategory}">
                <p @click.prevent="activeCategory = idx">
                  <span class="category-icon" v-html="cat.icon"></span>
                  {{ cat.label }}
                </p>
              </li>
            </ul>
          </div>

          <!-- Items -->
          <div class="epm-list">
            <ul>
              <li v-for="item in filteredItems" :key="item.type">
                <p @click.prevent="selectItem(item)">{{ item.label }}</p>
              </li>
            </ul>
          </div>
        </section>

        <!-- Footer -->
        <footer class="epm-footer">
          <button class="epm-footer__button" @click="close">Закрыть</button>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({ show: { type: Boolean, required: true } })
const emit = defineEmits(['update:show', 'select'])

let counter = 0
function makeId() { counter += 1; return Date.now() + counter }

// Fat иконки для категорий
const categories = [
  { label: 'Текстовые элементы', icon: `
    <svg viewBox="0 0 24 24" fill="currentColor">
      <rect x="3" y="6" width="18" height="2" rx="1"/>
      <rect x="3" y="11" width="18" height="2" rx="1"/>
      <rect x="3" y="16" width="18" height="2" rx="1"/>
    </svg>
  `},
  { label: 'Формы', icon: `
    <svg viewBox="0 0 24 24" fill="currentColor">
      <rect x="3" y="5" width="18" height="3" rx="1"/>
      <rect x="3" y="11" width="18" height="3" rx="1"/>
      <rect x="3" y="17" width="18" height="3" rx="1"/>
    </svg>
  `},
  { label: 'Выбор/загрузка', icon: `
    <svg viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4v10m-3-3l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <rect x="5" y="17" width="14" height="3" rx="1"/>
    </svg>
  `}
]

const items = [
  { category: 'Текстовые элементы', label: 'Заголовок', type: 'h2' },
  { category: 'Текстовые элементы', label: 'Параграф / описание', type: 'p' },
  { category: 'Текстовые элементы', label: 'Список', type: 'li' },
  { category: 'Формы', label: 'Текстовый ввод', type: 'input' },
  { category: 'Формы', label: 'Большой текстовый ввод', type: 'textarea' },
  { category: 'Формы', label: 'Выпадающий список', type: 'select' },
  { category: 'Формы', label: 'Радиокнопки', type: 'radio' },
  { category: 'Формы', label: 'Флажки', type: 'checkbox' },
  { category: 'Формы', label: 'Выбор даты/времени', type: 'datetime' },
  { category: 'Формы', label: 'Слайдер / Диапазон', type: 'range' },
  { category: 'Выбор/загрузка', label: 'Загрузка файлов', type: 'file' },
]

const activeCategory = ref(0)
const filteredItems = computed(() => items.filter(i => i.category === categories[activeCategory.value].label))

function buildDefaults(item) {
  const base = { type: item.type, id: makeId(), label: item.label, value: '' }
  switch (item.type) {
    case 'h2': return { ...base, value: 'Заголовок', tag: 'h2' }
    case 'p': return { ...base, value: 'Абзац текста', tag: 'p' }
    case 'li': return { ...base, value: 'Пункт 1\nПункт 2', listType: 'ul' }
    case 'input': return { ...base, placeholder: 'Введите текст', inputType: 'text' }
    case 'textarea': return { ...base, placeholder: 'Введите текст', rows: 4 }
    case 'select': return { ...base, options: ['Опция 1','Опция 2'] }
    case 'radio': return { ...base, options: ['Вариант A','Вариант B'], value: '' }
    case 'checkbox': return { ...base, options: ['Фича 1','Фича 2'], value: [] }
    case 'datetime': return { ...base, picker: 'date' }
    case 'file': return { ...base, files: [] }
    case 'range': return { ...base, value:0, min:0, max:100 }
    default: return base
  }
}

function selectItem(item) {
  const el = buildDefaults(item)
  emit('select', el)
  emit('update:show', false)
}

function close() { emit('update:show', false) }
</script>

<style scoped>
.epm-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.4);
  z-index: 1000000;
}

.epm-modal {
  width: 720px;
  max-width: 95%;
  background-color: var(--grey-1);
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
	height: 30rem;
  overflow: hidden;
}

.epm-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
}
.epm-title { margin: 0; font-size: 2rem }
.epm-close { background: transparent; border: none; font-size: 2rem; cursor: pointer; color: var(--color-text) }
.epm-close:hover { opacity: 0.5 }

.epm-body {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  overflow: hidden;
	height: 100%;
}

.epm-categories {     
	width: 14rem;
  overflow-y: auto;
  border-right: 0.2rem solid var(--grey-0);
  padding-right: 1rem; 
}
.epm-categories ul { list-style: none; padding:0; margin:0 }
.epm-categories li { margin-bottom:6px }
.epm-categories li p {
  cursor:pointer;
  display:flex;
  align-items:center;
  margin: 0; 
  gap:6px;
  padding:8px 10px;
  border-radius:6px;
  text-align: left;
}
.epm-categories li.active p { background: var(--grey-0); text-align: left; }

.category-icon {
	height: 1rem;
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
}
.category-icon svg {
  width: 20px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
  fill: currentColor;
}

.epm-list { flex: 1; overflow-y: auto }
.epm-list ul { list-style:none; padding:0; margin:0; display:grid; grid-template-columns:repeat(2,1fr); gap:8px }
.epm-list li p { margin: 0; height: 100%; cursor:pointer; padding:10px; border:1px solid #eee; border-radius:6px }

.epm-footer { padding:1rem; border-top:1px solid #eee; font-size:12px; color:#666; margin-top:auto }
.epm-footer__button { margin-left:auto; }

@media (max-width: 768px) {
  .epm-body {
    display: flex;
    flex-direction: column;
    width: 100%;
    flex-wrap: nowrap;
  }
  .epm-categories {
    display: flex;
    flex-direction: row;
    border-right: none;
    flex-wrap: nowrap;
    border-bottom: 0.2rem solid var(--grey-0);
    width: 100%;
  }
  .epm-categories ul {
    display: flex;
    width: 100%;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
  }
  .epm-categories ul li p {
    display: flex;
    align-items: center;
    flex-direction: row;
    flex-wrap: nowrap;
    line-height: 0;
    text-wrap: nowrap;
  }
}
</style>
